<template>
  <div class="px-2 dispense-request-page" v-if="hasStorePermission">
    <vs-popup class="holamundo stripe-dialog" title="Add payment method" :active.sync="showAddPayment" button-close-hidden>
      <vs-tabs>
        <vs-tab label="Credit Card" @click="paymentType = 'CreditCard'">
          <stripe-elements ref="stripeElement" v-bind:title="saveCreditCard" :pk="stripePublishableKey"
            :amount="totalSelectedProductsCost" locale="en" @token="tokenCreated" @loading="loading = $event">
          </stripe-elements>
          <div class="flex gap-4 justify-end">
            <vs-button
              @click="showAddPayment=false"
              class="font-semibold px-6 py-4"
              type="flat"
            >Cancel</vs-button>
            <vs-button
              @click="submit"
              class="px-8 py-4 rounded-lg font-semibold"
              color="primary"
            >Save</vs-button>
          </div>
        </vs-tab>
        <vs-tab label="Bank Account" @click="paymentType = 'ClinicBankAccount'">
          <BankSetup v-if="selectedClinic" :totalPrice="totalSelectedProductsCost" :financialDetails="financialDetails"
            :isNewClinicBankAccount="isNewClinicBankAccount" :shippingDetails="shippingDetails" :paymentEmail="selectedClinic.paymentEmail"
            :from-list="true"
            :paymentType="'ClinicBankAccount'" :repayment="true" :buttonTitle="'Save'" :clinicId="clinicId"
            @onClinicBankPaymentSuccess="clinicBankPaymentSuccess"></BankSetup>
        </vs-tab>
      </vs-tabs>
    </vs-popup>
    <vs-popup id="custom-dialog-v2"  title="" :active.sync="validationRequest" button-close-hidden>
      <vs-card v-if="activateValidationOption && activateValidationOption.status">
        <div
          class="w-12 h-12 mb-4 bg-yellow-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-opacity-75"
          @click="handleRemovePatient(patientIndex)"
        >
          <feather-icon icon="InfoIcon" svgClasses="h-7 w-7" class="text-yellow-700" />
        </div>
        <p class="text-2xl font-bold mb-4">{{ activateValidationOption.message }}</p>
        <p class="mb-10 text-muted" style="font-size: 16px;">{{ activateValidationOption.description }}</p>
        <div  iv class="flex flex-col justify-end w-full">
          <div class="flex gap-4 justify-end">
            <vs-button
              @click="handleValidateAction(false)"
              class="font-semibold px-6 py-4"
              type="flat"
            >{{ activateValidationOption.options[0] }}</vs-button>
            <vs-button
              @click="handleValidateAction(true)"
              class="px-8 py-4 rounded-lg font-semibold"
              color="primary"
            >{{ activateValidationOption.options[1] }}</vs-button>
          </div>
        </div>
      </vs-card>
    </vs-popup>
    <div
      class="flex items-center gap-2 cursor-pointer"
      @click.prevent="handleBackRouteEvent"
    >
      <vs-icon icon="keyboard_backspace" size="25px"></vs-icon>
      <p class="font-bold">Back</p>
    </div>
    <div class="info-card">
      <span class="info-icon">
        <vs-icon icon="info_outline" size="25px"></vs-icon>
      </span>
      <div class="info-content">
        <h3 class="info-title">How the Dispense Request Works</h3>
        <p class="info-description">
          This form is used to request the dispensing and delivery of restricted medications
          used by clinics who have adopted the pharmacy dispense model in Queensland.
        </p>
        <findOutMore/>
      </div>
    </div>
    <div class="max-w-7xl mx-auto py-2 grid grid-cols-1 md:grid-cols-2 gap-10">
      <div v-if="!hasReachedPaymentOptions" >
        <div class="flex gap-2 flex-col mb-5">
          <h1 class="text-xl font-semibold text-green-900">New Dispense Request</h1>
          <p class="text-muted mt-1 text-base">Build your dispense request</p>
        </div>
        <div class="mb-6" v-if="currentUserIsSuperAdmin()">
          <label for="clinic" class="block text-base font-semibold text-gray-800 mt-6 mb-1">
            Choose an organisation
          </label>
          <vs-select
            autocomplete  
            v-model="organizationId"
            class="w-full"
            placeholder="Organisation name"
            @click="handleOrganizationList"
            >
            <vs-select-item  :key="index" :value="item._id" :text="item.name" v-for="(item,index) in organizationsList" />
          </vs-select>
        </div>
        <div class="space-y-4 animate-pulse mt-6" v-if="isLoadingOrganizations">
          <div v-for="item in 3" :key="item" class="h-10 rounded-lg bg-gray-200 w-full"></div>
        </div>
        <div v-if="displayDropdownClinic" class="mb-6">
          <label for="clinic" class="block text-base font-semibold text-gray-800 mt-6 mb-1">
            Choose a clinic
          </label>
          <vs-select
            autocomplete  
            v-model="clinicId"
            class="w-full"
            placeholder="Clinic name"
            :disabled="!organizationId"
            @click="getClinics"
            @blur="handleBlurClinicSelection"
            >
            <vs-select-item  :key="index" :value="item.value" :text="item.text" v-for="(item,index) in clinicsList" />
          </vs-select>
        </div>
        <div v-else-if="selectedClinic && selectedClinic.value && !displayDropdownClinic" class="mb-6">
          <div class="clinic-box border border-solid p-4 py-5 w-full max-w-xl">
            <div class="py-1 flex justify-between items-start">
              <div>
                <p class="text-base font-semibold text-gray-900"> {{ selectedClinic.clinicName }}
                  <span class="font-normal text-muted ml-2">({{ selectedClinic.bestContactNumber || 'NA' }})</span>
                </p>
              </div>
              <feather-icon v-if="!displayDropdownClinic" icon="EditIcon" svgClasses="h-5 w-5" class="cursor-pointer"
                  @click="displayDropdownClinic = true"
               />
            </div>
            <vs-divider></vs-divider> 
            <div class="flex gap-2 flex-col">
              <p class="text-sm text-muted font-normal">Delivery address</p>
              <p class="text-base text-gray-900">{{ selectedClinic.address && selectedClinic.address.displayAddress || 'NA' }}</p>
            </div>
          </div>
        </div>
        <div class="patient-box mb-4" :key="patientIndex" v-for="(item, patientIndex) in selectedPatients">
          <div class="flex justify-between w-full items-center">
            <p class="text-bsae font-semibold">{{ item.patient.fullName }} <span class="text-muted font-normal">{{ item.patient.contactNumber }}</span></p>
            <div
              class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-opacity-75"
              @click="handleRemovePatient(patientIndex)"
            >
              <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" class="text-red-700" />
            </div>
          </div>
          <vs-divider class="m-0"></vs-divider> 
          <div class="flex flex-col gap-3">
            <p class="text-sm text-muted">Address</p>
            <p class="text-base text-standard-primary">{{ item.patient.address }}</p>
            <p  class="text-sm text-muted">Date of Birth</p>
            <p class="text-base text-standard-primary">{{ new Date(item.patient.dateOfBirth).toLocaleDateString('en-GB').replace(/\//g, ' / ') }}</p>
          </div>
          <vs-divider class="m-0"></vs-divider> 
          <template v-if="item.scripts.length">
            <div class="w-full" :key="scriptIndex" v-for="(script, scriptIndex) in item.scripts">
              <div class="flex flex-col gap-3 mb-2">
                <p class="text-base text-information font-semibold">TX - {{ script.authorisationNumber }}</p>
                <p class="text-sm text-muted">Treatment plans</p>
              </div>
              <div class="flex flex-col gap-3 text-standard-primary text-base font-normal">
                <p :key="index" v-for="(script, index) in script.treatmentPlans">
                  {{ index + 1 }}. {{  script.productName }} - {{ script.amount }} {{ script.productUnit }}
                </p>
              </div>
              <div class="flex flex-col gap-5 w-full">
                <p class="text-sm text-muted mt-4">Select Products</p>
                <div class="w-full flex flex-col gap-5" :key="treatmentIndex" v-for="(treatment, treatmentIndex) in script.treatmentPlans">
                  <div class="flex justify-between w-full" :key="treatmentProductIndex" v-for="(treatmentProduct, treatmentProductIndex) in treatment.products">
                    <div class="flex flex-col gap-2">
                      <p class="text-base font-normal text-new-primary">{{ treatmentProduct.productName }}</p>
                      <span class="text-muted text-base font-normal">${{ treatmentProduct.productStorePriceDisplay }}</span>
                    </div>
                    <div class="product-actions">
                      <span class="less-btn hover:bg-gray-100" @click="handleProductQuanity(treatmentProduct, -1)">
                        <vs-icon
                          size="20px"
                          class="cursor-pointer"
                          icon="remove"
                        >
                        </vs-icon>
                      </span>
                      <span class="qty-display text-new-primary text-base">{{ treatmentProduct.quantity }}</span>
                      <span class="add-btn hover:bg-gray-100" @click="handleProductQuanity(treatmentProduct, 1)">
                        <vs-icon
                          size="20px"
                          class="cursor-pointer"
                          icon="add"
                        >
                        </vs-icon>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <vs-alert v-else class="relative" color="warning" active="true" icon="info">
            <p class="font-normal">No active scripts found for this patient at this clinic location.</p>
          </vs-alert>
        </div>
        <div v-if="selectedClinic && selectedClinic.value">
            <label for="clinic" class="block text-base font-semibold text-gray-800 mt-6 mb-1">
              Add a patient for dispense request
            </label>
            <vs-select
              class="w-full"
              autocomplete
              :disabled="!clinicId"
              v-model="patientId"
              @click="getPatientsByClinic"
              @change="handlePatientOnSelect"
              placeholder="Search patient using first name, last name or phone number"
              >
              <vs-select-item :key="index" :value="item.value" :text="`${item.text} - ${item.contactNumber}`" v-for="(item,index) in unselectedPatients" />
            </vs-select>
        </div>
        <div class="space-y-4 animate-pulse mt-6" v-if="isLoadingPatientScript || isLoadingPatients || isLoadingClinics">
          <div v-for="item in 3" :key="item" class="h-10 rounded-lg bg-gray-200 w-full"></div>
        </div>
      </div>
      <div v-else>
        <div class="flex gap-2 flex-col mb-5">
          <h1 class="text-xl font-semibold text-green-900">Payment Options</h1>
          <p class="text-muted mt-1 text-base">Select payment method</p>
        </div>
        <div class="w-full mx-auto mt-2 p-4 border-solid border border-gray-400 rounded-lg">
          <div class="space-y-4">
            <label
              class="flex items-start gap-3 cursor-pointer"
              v-for="method in selectedClinicPaymentMethods"
              :key="method.paymentMethodId"
              @click="selectMethod(method)"
            >
              <div
                :class="[ ' border-solid w-6 h-6 rounded-full border-1 flex items-center justify-center',
                selectedMethod && selectedMethod.paymentMethodId === method.paymentMethodId
                    ? 'bg-green-800'
                    : 'border-green-800'
                ]"
              >
                <div
                  v-if="selectedMethod && selectedMethod.paymentMethodId === method.paymentMethodId"
                  class="w-2 h-2 bg-white rounded-full"
                ></div>
              </div>
              <div v-if="method.type === 'card'">
                <p class="font-semibold capitalize">{{ method.card.brand }}</p>
                <p class="text-sm text-gray-500">XXXX XXXX XXXX {{ method.card.last4 }}</p>
                <p class="text-sm text-gray-500">{{ method.card.exp_month }} / {{ method.card.exp_year }}</p>
              </div>
              <div v-if="method.type === 'au_becs_debit'">
                <p class="font-semibold">Australia BECS Direct Debit</p>
                <p class="text-sm text-gray-500">XXXX XXXX XXXX {{ method.au_becs_debit.last4 }}</p>
                <p class="text-sm text-gray-500">
                  BSB number {{ method.au_becs_debit.bsb_number }}
                </p>
              </div>
            </label>
          </div>
          <vs-divider></vs-divider> 
          <button
            @click="showAddPayment=true"
            class="cursor-pointer flex items-center gap-2 px-6 py-2 bg-white border border-solid border-gray-700 text-green-900 font-medium rounded-lg hover:bg-gray-100"
          >
            <span class="text-xl">+</span> Add payment method
          </button>
        </div>
        <div class="order-notes-section py-4 px-2">
          <vs-divider></vs-divider> 
          <p class="text-lg font-bold mb-2">Order Notes</p>
          <p class="text-sm">Add order notes for the pharmacy.</p>
          <vs-textarea name="notes" counter="100" max="100" v-model="orderNotes" class="w-full h-50 mt-3 placeholder-sm placeholder-gray-500" rows="10" 
          placeholder="You can add delivery preferences here and we’ll do our best to accommodate. Please note:

• We may be able to avoid certain days (for example, “not Tuesday”), but this isn’t always guaranteed.
• We cannot support requests for exact times, after-hours delivery, or driver call/text. Deliveries occur during standard business hours."
          >
          </vs-textarea>
        </div>
      </div>
      <div class="summary-section sticky self-start z-0">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">Summary</h2>
        <div v-if="!selectedProductsWithQuanity.length" class="border border-solid rounded-lg text-base items-header px-2 py-3 mb-4">
          <table class="w-full text-left text-sm">
            <thead>
              <tr class="border-b">
                <th style="width:60%" class="font-semibold text-black py-2 pl-0 text-base text-left">Item</th>
                <th class="font-semibold text-black py-2 text-center text-base">Qty.</th>
                <th class="font-semibold text-black py-2 pr-0 text-right text-base">Unit price</th>
              </tr>
            </thead>
          </table>
        </div>
        <div class="border border-solid rounded-lg text-base items-header px-4 py-5 mb-4" :key="productIndex" v-for="(product, productIndex) in selectedProductsWithQuanity">
          <div>
            <div class="flex justify-between items-center">
              <p class="font-semibold text-base text-new-primary">{{ product.patientName }}</p>
              <span  v-if="hasReachedPaymentOptions" @click.prevent="selectedPatients[productIndex].show_details=!selectedPatients[productIndex].show_details">
                <vs-icon class="cursor-pointer" :icon="selectedPatients[productIndex].show_details ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" size="25px"></vs-icon>
              </span>
            </div>
            <vs-divider></vs-divider> 
            <template v-if="hasReachedPaymentOptions && selectedPatients[productIndex].show_details">
              <div class="flex flex-col gap-3">
                <p class="text-sm text-muted">Address</p>
                <p class="text-base text-standard-primary">{{ product.address }}</p>
                <p  class="text-sm text-muted">Date of Birth</p>
                <p class="text-base text-standard-primary">{{ new Date(product.dateOfBirth).toLocaleDateString('en-GB').replace(/\//g, ' / ') }}</p>
              </div>
              <vs-divider></vs-divider> 
            </template>
          </div>
          <div class="flex flex-col gap-0 mb-4" :key="productScriptsIndex" v-for="(productScript, productScriptsIndex) in product.scripts">
            <p class="text-information text-base font-semibold">TX - {{ productScript.scriptNumber }}</p>
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="border-b">
                  <th style="width:60%" class="font-semibold text-black py-2 pl-0 text-base text-left">Item</th>
                  <th class="font-semibold text-black py-2 text-center text-base">Qty.</th>
                  <th class="font-semibold text-black py-2 pr-0 text-right text-base">Unit price</th>
                </tr>
              </thead>
              <tbody class="text-standard-primary">
                <tr class="border-b" :key="productScriptItemIndex" v-for="(productScriptItem, productScriptItemIndex) in productScript.items">
                  <td class="py-2 text-base font-light">{{ productScriptItem.productName }}{{ productScriptItem.productStoreIsTaxable ? '*' : '' }}</td>
                  <td class="py-2 text-base text-center font-light">{{ productScriptItem.quantity }}</td>
                  <td class="py-2 text-base text-right font-light">${{ productScriptItem.productStorePriceDisplay }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="flex flex-col gap-3 my-6" v-if="currentUserIsSuperAdmin()">
            <div>
              <label class="font-bold text-sm">Price Adjustment</label>
              <vs-input type="number" class="w-full" v-model="subtotalAdjustment" placeholder="$0.00">
              </vs-input>
            </div>
            <div>
              <label class="font-bold text-sm">Patient Dispense Fee</label>
              <vs-input type="number" class="w-full" v-model="patientDispenseFeeAdjustment" placeholder="$0.00"></vs-input>
            </div>
        </div>
        <vs-divider></vs-divider> 
        <div class="space-y-3  text-sm text-gray-700">
          <div class="flex justify-between">
            <span class="text-green-900 text-base">Patient Dispense Fee* ({{ selectedPatientWithProductsCount }})</span>
            <span class="text-base">${{ patientDispenseFeeTotalDisplay }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-green-900 text-base">Subtotal</span>
            <span class="text-base">${{ displayTotalSelectedProductsCostExcludingGst }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-green-900 text-base">GST (10%)</span>
            <span class="text-base">${{ displayTotalGst }}</span>
          </div>
        </div>
        <vs-divider></vs-divider> 
        <div class="flex  justify-between font-semibold text-green-900">
          <span class="text-lg">Total</span>
          <span>${{displayTotalProductsCost}}</span>
        </div>
        <div class="mt-8 flex justify-end">
            <vs-button
              class="w-full sm:w-auto"
              :disabled="isSubmitDisabled"
              @click="handleSubmitRequest"
            >
              <div class="flex items-center gap-2 justify-center">
                <p>{{ hasReachedPaymentOptions ? 'Submit request' : 'Review request' }}</p>
                <vs-icon
                  size="20px"
                  class="cursor-pointer"
                  :icon="hasReachedPaymentOptions ? 'credit_card' : 'arrow_forward'"
                >
                </vs-icon>
              </div>
            </vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { formatPriceValue } from '../../../utils/format';
import findOutMore from './findOutMore.vue';
import BankSetup from '../../components/order-center/BankSetup.vue';
import StripeElements from '../../components/order-center/StripeElements.vue';
import permissionMixin from '@/mixins/permissionMixin';
import { routePrefix } from '@/globalHelpers/routes';
import { currentUserIsSuperAdmin } from '@/globalHelpers/userRole';

const ORDER_NOTES_MAX_LENGTH = 100;
export default {
  name: 'DispenseRequestPage',
  mixins: [permissionMixin],
  components: {
    findOutMore,
    StripeElements,
    BankSetup,
  },
  data() {
    return {
      validationRequest:false,
      validationPopupOptions: [
        {
          type: 'exit_page',
          message: 'You have unsaved changes',
          description: 'All progress in this session will be lost. Are you sure to want to leave?',
          status: false,
          options: ['Keep editing', 'Leave'],
        },
        {
          type: 'reload_page',
          message: 'Reloading the page will clear your dispense request. Do you want to continue?',
          status: false,
          options: ['Cancel', 'Reload anyway'],
        },
        {
          type: 'remove_patient',
          message: 'Remove patient?',
          description: 'Removing this patient will also delete their selected products. Are you sure?',
          status: false,
          options: ['Keep patient', 'Remove'],
        },
      ],
      confirmRequestDialog:false,
      displayDropdownClinic:true,
      popupActivo:false,
      clinicId:'',
      patientId: '',
      select2: 0,
      clinicsList: [],
      organizationsList: [],
      patientsList: [],
      organizationId:null,
      isLoadingClinics:false,
      isLoadingOrganizations: false,
      isLoadingPatients:false,
      isLoadingPatientScript:false,
      isCreatingDispenseRequest:false,
      selectedPatients: [],
      productListSummary:[],
      dispenseRequestFee: {
        price: 0,
      },
      clinicModel: 'Model 3',
      selectedPatientIndexToDelete:null,
      hasReachedPaymentOptions:false,
      selectedClinicPaymentMethods:[],
      selectedMethod: null,
      showAddPayment: null,
      paymentType: null,
      financialDetails: null,
      shippingDetails: null,
      isNewClinicBankAccount: false,
      loading: false,
      saveCreditCard: true,
      stripePublishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      updater: null,
      dispenseDefaultErrorMessage: 'An error occurred while creating the dispense request. Please try again later.',
      orderNotes:'',
      subtotalAdjustment: null,
      patientDispenseFeeAdjustment: null,
    };
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

  },
  beforeRouteLeave(to, from, next) {
    this.handleOnBackEvent(next);
  },
  methods: {
    currentUserIsSuperAdmin,
    ...mapActions('organization', ['fetchOrganizationClinics', 'fetchOrganizations']),
    ...mapActions('orgOwner', ['getPatients']),
    ...mapActions('dispenseRequest', ['getPatientScripts', 'getDispenseRequest', 'createNewDispenseRequest', 'getDispenseRequestFee']),
    ...mapActions('clinic', [
      'addNewPaymentMethod',
      'getClinicStripePaymentMethods',
    ]),
    handleBeforeUnload(event) {
      if (this.totalSelectedProductsCost > 0) {
        event.preventDefault();
        event.returnValue = ''; // Required for Chrome to show the confirmation dialog
      }
    },
    async tokenCreated(token) {
      this.$vs.loading();

      const data = {
        updater: this.updater.firstName + ' ' + this.updater.lastName,
        name: this.selectedClinic.clinicName,
        updatedFields: {
          card: {
            oldValue: '', newValue: `${token.card.brand} (XXXX XXXX XXXX ${token.card.last4})`,
          },
        },
      };
      
      try {
        await this.addNewPaymentMethod({
          data,
          token,
          clinicId: this.clinicId,
          intentId: null,
        });

        this.showAddPayment = false;
        await this.fetchClinicStripePaymentMethods(this.clinicId);
      } catch (err) {
        this.$vs.notify({
          title: err.data.title || 'Error',
          text: err.data.message || 'An unexpected error occurred.',
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async getClinics() {
      if (this.clinicsList.length) return;

      this.isLoadingClinics = true;

      try {
        const res = await this.fetchOrganizationClinics({
          id: this.organizationId,
          limit: 999999,
          model: this.clinicModel,
        });

        this.clinicsList = res.data.data.docs.map((clinic) => ({
          value: clinic._id,
          text: clinic.clinicName,
          sageClientId: clinic.sageClientId,
          bestContactNumber: clinic.bestContactNumber,
          clinicName: clinic.clinicName,
          address: clinic.address,
          paymentEmail: clinic.storeInvoiceEmail || clinic.invoiceEmail,
        }));
      } finally {
        this.isLoadingClinics = false;
      }
    },
    async getPatientsByClinic() {
      if (this.patientsList.length || !this.clinicId) return;

      this.isLoadingPatients = true;

      try {
        const res = await this.getPatients({
          id: this.organizationId,
          limit: 999999,
        });

        this.patientsList = res.data.data.docs.map((patient) => ({
          value: patient._id,
          text: patient.fullName,
          ...patient,
        }));
      } catch (err) {
        this.$vs.notify({
          title: 'Failed Fetching Patient Treatment Authorities',
          text: 'An error occurred while creating the dispense request. Please try again later.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
        });
      } finally {
        this.isLoadingPatients = false;
      }
    },
    async handlePatientOnSelect(patientId) {
      if (!patientId) return;

      this.isLoadingPatientScript = true;

      try {
        const { data } = await this.getPatientScripts({ patientId, clinicId: this.clinicId });
        const { scripts, treatmentProducts } = data.data;

        const patient = this.patientsList.find(({ _id }) => _id === patientId);

        const formattedScripts = scripts.map((script) => {
          return {
            ...script,
            treatmentPlans: script.treatmentPlans.map((plan) => {
              const products = treatmentProducts[plan.productId] || [];

              const formattedProducts = products.map((product) => ({
                ...product,
                quantity: 0,
                productStorePriceDisplay: formatPriceValue(product.productStorePrice),
              }));

              return {
                ...plan,
                products: formattedProducts,
              };
            }),
          };
        });

        this.selectedPatients.push({
          patient,
          scripts: formattedScripts,
          show_details: false,
        });

        this.patientId = '';
      } catch (error) {
        this.patientId = '';
      } finally {
        this.isLoadingPatientScript = false;
      }
    },
    async handleOrganizationList() {
      try {
        if (this.organizationsList.length) return;

        this.isLoadingOrganizations = true;
        const res = await this.fetchOrganizations({
          page: 1,
          limit: 999999,
          search:'',
          sort: 'name',
          dir: 'asc',
          filter: 'Approved',
        });

        this.organizationsList = res.data.data.docs;
      } finally {
        this.isLoadingOrganizations = false;
      }
    },
    getTotalProducts(treatments) {
      return treatments.reduce((total, treatment) => {
        const products = treatment.products || [];
        return total + products.length;
      }, 0);
    },
    getTotalProductsWithQuanity(treatments) {
      return treatments.reduce((total, treatment) => {
        const products = treatment.products || [];
        return total + products.filter(p => p.quantity > 0).length;
      }, 0);
    },
    resetValidationStatuses() {
      this.validationPopupOptions = this.validationPopupOptions.map((item) => {
        return {
          ...item,
          status:false,
        };
      });
    },
    handleOnBackEvent(next) {
      this.resetValidationStatuses();

      if (this.totalSelectedProductsCost > 0) {
        this.validationRequest = true;
        this.validationPopupOptions[0].status = true;
        return;
      }

      next();
    },
    handleRemovePatient(patientIndex, force = false) {
      this.resetValidationStatuses();
      this.selectedPatientIndexToDelete = patientIndex;

      if (!force) {
        const selectedPatient = this.selectedPatients[patientIndex];
        const isPatientHasProducts = selectedPatient.scripts.some(script=>script.treatmentPlans.some(treatment=>treatment.products.some((product) => product.quantity > 0)));
        
        if (isPatientHasProducts) {
          this.validationRequest = true;
          this.validationPopupOptions[2].status = true;
          return;
        }
      }

      this.selectedPatients.splice(patientIndex, 1);
    },
    handleProductQuanity(treatmentProduct, quantity) {
      if (treatmentProduct.quantity + quantity < 0 ) return;

      treatmentProduct.quantity += quantity;
    },
    handleOnCreateDispenseRequest() {
      if (!this.canCreateDispenseRequest() || this.orderNotes.length > ORDER_NOTES_MAX_LENGTH) return;

      this.$vs.loading();
      this.isCreatingDispenseRequest = true;

      const payload = this.buildDispenseRequestPayload();

      this.createNewDispenseRequest(payload)
        .then(this.onDispenseRequestSuccess)
        .catch((error) => this.onDispenseRequestFailure(error))
        .finally(this.onDispenseRequestComplete);
    },
    canCreateDispenseRequest() {
      const hasProducts = this.totalSelectedProductsCost > 0 && this.selectedMethod;
      const hasAdjustments = this.subtotalAdjustment && this.patientDispenseFeeAdjustment;

      return hasProducts || hasAdjustments;
    },
    buildDispenseRequestPayload() {
      const clinic = this.clinicsList.find((clinic) => clinic.value === this.clinicId);
      const toCentsOrNull = (value) => value == null || value === '' ? null : Number(value * 100);

      return {
        orderNotes: this.orderNotes,
        paymentMethodId: this.selectedMethod.paymentMethodId,
        clinicId: this.clinicId,
        organizationId: this.organizationId,
        clinicSageClientId: clinic.sageClientId,
        subtotalAdjustment:  toCentsOrNull(this.subtotalAdjustment),
        patientDispenseFeeAdjustment: toCentsOrNull(this.patientDispenseFeeAdjustment),
        dispenseRequests: this.buildDispenseRequests(),
      };
    },
    buildDispenseRequests() {
      return this.selectedPatients
        .filter(this.hasSelectedProducts)
        .map((patient) => ({
          patientId: patient.patient._id,
          patientName: patient.patient.fullName,
          dispenseFee: this.buildDispenseFee(),
          items: this.buildDispenseItems(patient),
        }));
    },
    hasSelectedProducts(patient) {
      return patient.scripts.some(script =>
        script.treatmentPlans.some(treatment =>
          treatment.products.some(product => product.quantity > 0)
        )
      );
    },
    buildDispenseFee() {
      return {
        sageItemId: this.dispenseRequestFee.sageItemId,
        quantity: 1,
        price: this.dispenseRequestFee.price * 100,
        itemName: 'Patient Dispense Fee',
      };
    },
    buildDispenseItems(patient) {
      return patient.scripts.flatMap(script =>
        script.treatmentPlans.flatMap(plan =>
          plan.products
            .filter(product => product.quantity)
            .map(product => ({
              quantity: product.quantity,
              productId: product.productStoreId,
              treatmentAuthorityId: script.authorisationId,
            }))
        )
      );
    },
    onDispenseRequestSuccess() {
      this.resetDispenseRequestForm();
      this.$vs.notify({
        title: 'Success!',
        text: 'Dispense request created successfully.',
        iconPack: 'feather',
        icon: 'icon-check',
        color: 'success',
      });
      this.$router.push({ name: `${routePrefix()}DispenseRequestList` });
    },
    onDispenseRequestFailure(error) {
      this.$vs.notify({
        title: 'Failed Creating Dispense Request',
        text: error.data && error.data.message || this.dispenseDefaultErrorMessage,
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
      });
    },
    onDispenseRequestComplete() {
      this.isCreatingDispenseRequest = false;
      this.$vs.loading.close();
    },
    resetDispenseRequestForm() {
      this.clinicId = '';
      this.confirmRequestDialog = false;
      this.selectedPatients = [];
      this.subtotalAdjustment = null;
      this.patientDispenseFeeAdjustment = null;
    },
    async fetchDispenseRequestFee() {
      const res = await this.getDispenseRequestFee();
      this.dispenseRequestFee = res.data.data[0].value;
    },
    handleValidateAction(status) {
      this.validationRequest = false;
      
      this.$nextTick(() => {
        const activeOption = this.validationPopupOptions.find((item) => item.status);

        if (status && activeOption.type === 'remove_patient') {
          this.handleRemovePatient(this.selectedPatientIndexToDelete, true);
          return;
        }

        if (status && activeOption.type === 'exit_page') {
          this.subtotalAdjustment = null;
          this.patientDispenseFeeAdjustment = null;
          this.selectedPatients = [];
          this.$router.push({ name: `${routePrefix()}DispenseRequestList` });
        }
      });
    },
    handleBlurClinicSelection() {
      setTimeout(() => {
        if (this.displayDropdownClinic && this.clinicId) {
          this.displayDropdownClinic = false;
        }
      }, 150);
    },
    fetchClinicStripePaymentMethods(clinicId) {
      this.getClinicStripePaymentMethods({ clinicId })
        .then((res) => {
          if (res.data) this.selectedClinicPaymentMethods = res.data.data;
          this.selectedMethod = res.data.data.find((method) => method.isDefault);
        });
    },
    handleBackRouteEvent() {
      if (this.hasReachedPaymentOptions) {
        this.hasReachedPaymentOptions = false;
        return;
      }

      this.$router.push({ name: `${routePrefix()}DispenseRequestList` });
    },
    clinicBankPaymentSuccess() {
      this.$vs.loading.close();
      this.showAddPayment = false;
      this.fetchClinicStripePaymentMethods(this.clinicId);
    },
    selectMethod(method) {
      this.selectedMethod = method;
      this.isOpen = false;
    },
    handleSubmitRequest() {
      if (!this.hasReachedPaymentOptions) {
        this.hasReachedPaymentOptions = true;
        return;
      }

      this.handleOnCreateDispenseRequest();
    },
    submit() {
      this.$refs.stripeElement.submit();
      this.paymentType = 'CreditCard';
    },
    async getDispenseRequestDetails() {
      const dispenseRequestId = this.$route.query.id;

      if(!currentUserIsSuperAdmin() || !dispenseRequestId) return;


      try {
        if (this.organizationsList.length) return;

        this.isLoadingOrganizations = true;

        const res = await this.getDispenseRequest({ id: dispenseRequestId });

        this.populateDispenseForm(res.data.data);
      } finally {
        this.isLoadingOrganizations = false;
      }
    },
    async populateDispenseForm(dispenseData) {
      try {
        this.$vs.loading();

        await this.handleOrganizationList();
        this.organizationId = dispenseData.organizationId;

        await this.getClinics();
        this.clinicId = dispenseData.clinicId;

        await this.getPatientsByClinic();

        this.subtotalAdjustment = dispenseData.subTotalPriceAdjustment != null 
          ? (dispenseData.subTotalPriceAdjustment / 100).toString() 
          : null;

        this.patientDispenseFeeAdjustment = dispenseData.dispenseFeeAdjustment != null 
          ? (dispenseData.dispenseFeeAdjustment / 100).toString() 
          : null;

        const patientIds = dispenseData.dispenseRequest.map(({patientId}) => patientId);

        for (const patientId of patientIds ) {
          await this.handlePatientOnSelect(patientId);
        }

        this.updateDispenseQuantities(dispenseData);
      } finally {
        this.$vs.loading.close();
      }
    },
    updateDispenseQuantities(dispenseData) {
      dispenseData.dispenseRequest.forEach((dispense) => {
        const { patientId, items } = dispense;

        items.forEach(({ itemId, quantity, treatmentAuthorityId }) => {
          this.updatePatientProductQuantity(patientId, treatmentAuthorityId, itemId, quantity);
        });
      });
    },
    updatePatientProductQuantity(patientId, treatmentAuthorityId, itemId, quantity) {
      const patient = this.selectedPatients.find(
        ({ patient }) => patient._id === patientId
      );
      if (!patient) return;

      const script = patient.scripts.find(
        ({ authorisationId }) => authorisationId === treatmentAuthorityId
      );
      if (!script) return;

      const plan = script.treatmentPlans.find((plan) =>
        plan.products.some(({ productStoreId }) => productStoreId === itemId)
      );
      if (!plan) return;

      const product = plan.products.find(
        ({ productStoreId }) => productStoreId === itemId
      );
      if (!product) return;

      product.quantity = quantity;
    },
  },
  computed: {
    isSubmitDisabled() {
      const hasNotesExceededLimit = this.hasOrderNotesReachedMaxLimit;
      const hasAdjustments =
        this.subtotalAdjustment === '0' &&
        this.patientDispenseFeeAdjustment === '0';
      const noProductsSelected = !this.totalSelectedProductsCost && !hasAdjustments;
      const noPaymentMethod = this.hasReachedPaymentOptions && !this.selectedMethod && !hasAdjustments;

      return noProductsSelected || noPaymentMethod || hasNotesExceededLimit;
    },
    hasOrderNotesReachedMaxLimit() {
      return this.orderNotes.length > 100;
    },
    patientFeeGstValue() {
      return this.patientDispenseFeeTotal * this.gstRate;
    },
    activateValidationOption() {
      const option =  this.validationPopupOptions.find((item) => item.status);
      if (!option) return null;

      return option;
    },
    selectedClinic() {
      return this.clinicsList.find((clinic) => clinic.value == this.clinicId);
    },
    unselectedPatients() {
      const patients =  this.patientsList.filter((patient) => {
        return !this.selectedPatients.some((selectedPatient) => selectedPatient.patient._id === patient._id);
      });

      return patients;
    },
    selectedProductsWithQuanity() {
      let allProducts = this.selectedPatients.flatMap((selectedPatient) =>
        selectedPatient.scripts.flatMap((script) =>
          script.treatmentPlans.flatMap((plan) =>
            plan.products
              .filter((product) => product.quantity)
              .map((product) => {
                const productStoreGst =
                  product.productStorePrice * product.quantity * (product.productStoreIsTaxable ? this.gstRate : 0);

                return {
                  ...product,
                  ...script,
                  patientId: selectedPatient.patient._id,
                  patientName: selectedPatient.patient.fullName,
                  dateOfBirth: selectedPatient.patient.dateOfBirth,
                  address: selectedPatient.patient.address,
                  showPatientDetails: selectedPatient.show_details,
                  unit: plan.productUnit,
                  productStorePrice: product.productStorePrice,
                  productStorePriceDisplay: formatPriceValue(product.productStorePrice),
                  productStoreGst,
                  productStoreGstDisplay: formatPriceValue(productStoreGst),
                };
              })
          )
        )
      );
      
      const productMap = {};

      for (const product of allProducts) {
        const { 
          patientId: patientKey,
          authorisationNumber: scriptKey,
          productStorePrice,
          quantity,
          productStoreIsTaxable,
          productStorePriceDisplay,
          productName,
          patientName,
          address,
          dateOfBirth,
          showPatientDetails,
        } = product;

        if (!patientKey || !scriptKey) continue;

        const productStoreGst = productStorePrice * quantity * (productStoreIsTaxable ? this.gstRate : 0);

        const formattedProduct = {
          quantity,
          productStoreGst,
          productStoreGstDisplay: formatPriceValue(productStoreGst),
          productStoreIsTaxable,
          productStorePrice,
          productName,
          productStorePriceDisplay,
        };

        if (!productMap[patientKey]) {
          productMap[patientKey] = {
            patientName,
            address,
            dateOfBirth,
            showPatientDetails,
            patientId: patientKey,
            scripts: {},
          };
        }

        if (!productMap[patientKey].scripts[scriptKey]) {
          productMap[patientKey].scripts[scriptKey] = {
            scriptNumber: scriptKey,
            items: [],
          };
        }

        productMap[patientKey].scripts[scriptKey].items.push(formattedProduct);
      }

      const transformedPatients = Object.values(productMap).map(patient => {
        return {
          ...patient,
          scripts: Object.values(patient.scripts),
        };
      });

      return transformedPatients;
    },
    selectedProductsTotals() {
      if (this.subtotalAdjustment) {
        const adjustment = Number(this.subtotalAdjustment) * 100;
        return {
          costExGst: adjustment,
          gst: 0,
          total: adjustment,
        };
      }

      return this.selectedProductsWithQuanity.reduce(
        (totals, patient) => {
          patient.scripts.forEach(({ items }) => {
            items.forEach(({ quantity, productStorePrice, productStoreGst }) => {
              const price = Number(quantity) * Number(productStorePrice);
              const gst = Number(productStoreGst) || 0;

              totals.costExGst += price;
              totals.gst += gst;
              totals.total += price + gst;
            });
          });

          return totals;
        },
        { costExGst: 0, gst: 0, total: 0 }
      );
    },
    totalSelectedProductsCost() {
      let total = Number(this.selectedProductsTotals.total);

      if (this.hasItemSelected) {
        total += this.patientDispenseFeeTotal;
        total += this.patientFeeGstValue;
      }

      return total;
    },
    totalSelectedProductsCostExcludingGst() {
      let total = this.selectedProductsTotals.costExGst;

      if (this.hasItemSelected) {
        total += this.patientDispenseFeeTotal;
      }

      return total;
    },
    totalGst() {
      let total = this.selectedProductsTotals.gst;

      if (this.hasItemSelected) {
        total += this.patientFeeGstValue;
      }

      return total;
    },
    displayTotalGst() {
      return formatPriceValue(this.totalGst);
    },
    displayTotalSelectedProductsCostExcludingGst() {
      return formatPriceValue(this.totalSelectedProductsCostExcludingGst);
    },
    displayTotalProductsCost() {
      return formatPriceValue(this.totalSelectedProductsCost);
    },
    hasItemSelected() {
      return this.selectedPatients.some((patient) => {
        return patient.scripts.some((script) => {
          return script.treatmentPlans.some((plan) => {
            return plan.products.some((product) => product.quantity > 0);
          });
        });
      });
    },
    selectedPatientWithProductsCount() {
      return this.selectedPatients.filter((patient) => {
        return patient.scripts.some((script) =>
          script.treatmentPlans.some((plan) =>
            plan.products.some((product) => product.quantity > 0)
          )
        );
      }).length;
    },
    gstRate() {
      return parseFloat(process.env.GST_VALUE || '0.10');
    },
    patientDispenseFeeTotal() {
      if (!this.selectedPatients.length || !this.dispenseRequestFee.price) return 0;

      if (this.patientDispenseFeeAdjustment) return this.patientDispenseFeeAdjustment * 100;

      return (this.dispenseRequestFee.price * this.selectedPatientWithProductsCount) * 100;
    },
    patientDispenseFeeTotalDisplay() {
      return formatPriceValue(this.patientDispenseFeeTotal);
    },
  },
  watch: {
    clinicId(value) {
      if (value) this.fetchClinicStripePaymentMethods(value);
      
      this.displayDropdownClinic = !value;
      this.patientsList = [];
      this.selectedPatients = [];
      this.patientId = '';
    },
    organizationId(value) {
      if (value) this.clinicId = '';
    },
  },
  created() {
    this.fetchDispenseRequestFee();
    this.updater = JSON.parse(
      localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`)
    );
    this.getDispenseRequestDetails();
  },
  beforeMount: async function () {
    if (
      localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`)
    ) {
      this.organizationId = localStorage.getItem(
        `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`
      );
    }
  },
};
</script>
<style lang="scss">
@import "@/assets/scss/vuexy/components/popup-dialogv2.scss";

.con-select {
  margin: 0px !important;
}
.icon-select {
  font-weight: bold;
  right: 10px;
  font-size: 18px;
}
.selectExample {
  margin: 10px;
}
.con-select-example {
  display: flex;
  align-items: center;
  justify-content: center;
}
.con-select .vs-select {
  width: 100%
}

@media (max-width: 550px) {
  .con-select {
    flex-direction: column;
    width: 100% !important;
  }
  .con-select .vs-select {
    width: 100%
  }
}

.info-card {
  width: 100%;
  background-color: #EAF5FB;
  border-radius: 6px;
  border: 1px solid #95BFD7;
  padding: 15px;
  margin: 20px auto;
  position: relative;
}

.info-icon {
  font-size: 24px;
  color: #0a66c2;
  flex-shrink: 0;
  padding-top: 6px;
}

.info-content {
  flex: 1;
}

.info-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px;
}

.info-description {
  font-size: 14px;
  color: #636363;
  margin-bottom: 10px;
}

.info-button {
  background-color: rgba(255, 255, 255, 0.01);
  border: 1px solid rgba(0, 13, 77, 0.45);
  color: rgba(0, 9, 51, 0.65);
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0px 2px 4px -2px rgba(0, 0, 0, 0.08), 0px 4px 8px -2px rgba(0, 0, 0, 0.04);
} 

.info-button:hover {
  background-color: #f0f0f0;
}

.clinic-box {
  background-color: #F5FBF9; 
  border-color: #E3DDD7; 
  border-radius: 12px;
}

.text-muted {
  color: #828282;
}

.text-new-primary {
  color: #232323;
}

.text-standard-primary {
  color: #262928
}

.text-information {
  color: #125277;
}

.text-disabled {
  color: #969696;
}

.summary-section .items-header {
  background-color: #FAFAFA;
  border-color: #E3E3E3;
}

.patient-box {
  border-radius: 12px;
  border: 1px solid #E3DDD7;
  background: #FFF;
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.less-btn, .qty-display, .add-btn {
  border: 1px solid #969696;
  display: flex;
  width: 48px;
  height: 48px;
  padding: 0px;
  justify-content: center;
  align-items: center;
  gap: 0px;
  background: #FFF;
}

.less-btn {
  border-radius: 8px 0px 0px 8px;
  border-right: none;
}

.add-btn {
  border-radius: 0px 8px 8px 0px;
  border-left: none;
}

.product-actions {
  display: flex;
  flex-direction: row;
}

.summary-section {
  top: 120px !important;
  border-bottom: none !important;
}

.stripe-dialog .con-vs-tabs {
  padding: 20px;
}

.stripe-dialog .con-ul-tabs {
  margin-bottom: 20px;
}

.stripe-dialog .vs-popup {
  border-radius: 16px;
}

.placeholder-sm textarea::placeholder  {
  font-size: 0.90rem !important;
  color: #6b7280;
  white-space: pre-line;
}
</style>